import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { MessageSquare, Phone, Settings, CheckCircle, AlertCircle, ExternalLink, Copy, QrCode, Check, Zap, Loader2, ShieldCheck, Target, Bell, AlertTriangle, Sparkles, TrendingUp, Smartphone, BookOpen } from "lucide-react";
import { Input } from "@/components/ui/input";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { whatsappApi, accountSettingsApi } from "@/lib/api";
import { WHATSAPP_CONFIG } from "@/lib/constants";
import { useUser } from "@/contexts/UserContext";
import QRCode from "react-qr-code";

export default function WhatsAppConfig() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [isEnabled, setIsEnabled] = useState(false);
  const [transactionNotificationsEnabled, setTransactionNotificationsEnabled] = useState(false);
  const [showDisableDialog, setShowDisableDialog] = useState(false);
  const [isLoadingSettings, setIsLoadingSettings] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [monthlyBudgetLimit, setMonthlyBudgetLimit] = useState<string>("");
  const [savedBudgetLimit, setSavedBudgetLimit] = useState<string>(""); // Store budget when notifications are disabled
  const [isSavingBudget, setIsSavingBudget] = useState(false);
  const [averageMonthlyExpenses, setAverageMonthlyExpenses] = useState<number | null>(null);
  const [averageMonthlyIncome, setAverageMonthlyIncome] = useState<number | null>(null);
  const [suggestedBudgetLimit, setSuggestedBudgetLimit] = useState<number | null>(null);
  const [averageMonthsCount, setAverageMonthsCount] = useState<number | null>(null);
  
  const isRTL = i18n.language === 'he';

  // Get user data from context
  const { user, loading: isLoadingUser, syncBackendAccount } = useUser();
  const userData = user?.backendAccount;

  // Set initial toggle state based on user's whatsappEnabled status
  useEffect(() => {
    if (userData?.whatsappEnabled !== undefined) {
      setIsEnabled(userData.whatsappEnabled);
    }
  }, [userData?.whatsappEnabled]);

  // Fetch account settings on component mount
  useEffect(() => {
    const fetchAccountSettings = async () => {
      try {
        setIsLoadingSettings(true);
        const settings = await accountSettingsApi.getSettings();
        setTransactionNotificationsEnabled(settings.transactionNotificationsEnabled);
        if (settings.monthlyBudgetLimit) {
          setMonthlyBudgetLimit(settings.monthlyBudgetLimit.toString());
          setSavedBudgetLimit(settings.monthlyBudgetLimit.toString());
        }
        // Set AI-calculated averages
        setAverageMonthlyExpenses(settings.averageMonthlyExpenses);
        setAverageMonthlyIncome(settings.averageMonthlyIncome);
        setSuggestedBudgetLimit(settings.suggestedBudgetLimit);
        setAverageMonthsCount(settings.averageMonthsCount);
      } catch (error) {
        console.error('Failed to fetch account settings:', error);
      } finally {
        setIsLoadingSettings(false);
      }
    };

    if (!isLoadingUser && userData) {
      fetchAccountSettings();
    }
  }, [isLoadingUser, userData]);

  // Helper function to apply AI suggestion (75% of income)
  const handleApplySuggestion = () => {
    if (suggestedBudgetLimit) {
      setMonthlyBudgetLimit(suggestedBudgetLimit.toString());
      toast.success(t('whatsappConfig.suggestionApplied', 'AI suggestion applied!'));
    }
  };

  const handleEnableToggle = async (enabled: boolean) => {
    if (enabled) {
      if (!userData?.phoneNumber) {
        toast.error(t('whatsappConfig.phoneNumberRequired'));
        return;
      }
      // Just enable the local state to show instructions
      // Success toast will only show after verification
      setIsEnabled(true);
    } else {
      // Show confirmation dialog when disabling
      setShowDisableDialog(true);
    }
  };

  const handleVerifyConnection = async () => {
    try {
      setIsVerifying(true);
      
      // Call API to verify WhatsApp connection
      const result = await whatsappApi.verifyConnection();
      
      if (result.whatsappEnabled) {
        // Sync backend account to get updated WhatsApp status
        await syncBackendAccount();
        toast.success(t('whatsappConfig.enabledSuccess'));
      } else {
        toast.error(t('whatsappConfig.verifyFailed'));
      }
    } catch (error) {
      console.error('Failed to verify WhatsApp connection:', error);
      toast.error(t('whatsappConfig.verifyError'));
    } finally {
      setIsVerifying(false);
    }
  };

  const getVerificationMessage = () => {
    const accountId = userData?.accountId || 'unknown';
    return `verify-agent-${accountId}`;
  };

  const generateWhatsAppURL = () => {
    try {
      const message = encodeURIComponent(getVerificationMessage());
      const url = `${WHATSAPP_CONFIG.whatsappUrl}?text=${message}`;
      console.log('Generated WhatsApp URL:', url);
      return url;
    } catch (error) {
      console.error('Error generating WhatsApp URL:', error);
      return WHATSAPP_CONFIG.verifyUrl;
    }
  };

  const handleCopyMessage = () => {
    navigator.clipboard.writeText(getVerificationMessage());
    toast.success(t('whatsappConfig.messageCopied'));
  };

  const handleConfirmDisable = async () => {
    try {
      // Call API to disable WhatsApp integration
      await whatsappApi.disableIntegration();
      
      // Update local state
      setIsEnabled(false);
      setShowDisableDialog(false);
      
      toast.success(t('whatsappConfig.disabledSuccess'));
      
      // Sync backend account to get updated WhatsApp status
      await syncBackendAccount();
    } catch (error) {
      console.error('Failed to disable WhatsApp integration:', error);
      toast.error(t('whatsappConfig.disableError'));
      setShowDisableDialog(false);
    }
  };

  const handleCancelDisable = () => {
    setShowDisableDialog(false);
    // Keep the toggle in enabled state since user cancelled
  };

  const handleTransactionNotificationsToggle = async (enabled: boolean) => {
    try {
      setTransactionNotificationsEnabled(enabled);
      await accountSettingsApi.updateSettings({
        transactionNotificationsEnabled: enabled,
      });
      toast.success(
        enabled
          ? t('whatsappConfig.notificationsEnabled')
          : t('whatsappConfig.notificationsDisabled')
      );
    } catch (error) {
      console.error('Failed to update transaction notifications:', error);
      // Revert the toggle on error
      setTransactionNotificationsEnabled(!enabled);
      toast.error(t('whatsappConfig.notificationsUpdateError'));
    }
  };

  const handleBudgetNotificationsToggle = async (enabled: boolean) => {
    try {
      setIsSavingBudget(true);
      if (enabled) {
        // Re-enable: restore the saved budget or use suggested budget
        const budgetToRestore = savedBudgetLimit || (suggestedBudgetLimit?.toString() ?? "");
        if (budgetToRestore) {
          await accountSettingsApi.updateSettings({
            monthlyBudgetLimit: parseFloat(budgetToRestore),
          });
          setMonthlyBudgetLimit(budgetToRestore);
          toast.success(t('whatsappConfig.budgetNotificationsEnabled', 'Budget notifications enabled'));
        } else {
          toast.info(t('whatsappConfig.setBudgetFirst', 'Please set a budget limit first'));
        }
      } else {
        // Disable: save current budget and set to null
        if (monthlyBudgetLimit) {
          setSavedBudgetLimit(monthlyBudgetLimit);
        }
        await accountSettingsApi.updateSettings({
          monthlyBudgetLimit: null,
        });
        setMonthlyBudgetLimit("");
        toast.success(t('whatsappConfig.budgetNotificationsDisabled', 'Budget notifications disabled'));
      }
    } catch (error) {
      console.error('Failed to update budget notifications:', error);
      toast.error(t('whatsappConfig.notificationsUpdateError'));
    } finally {
      setIsSavingBudget(false);
    }
  };

  const handleSaveBudget = async () => {
    try {
      setIsSavingBudget(true);
      const budgetValue = monthlyBudgetLimit ? parseFloat(monthlyBudgetLimit) : null;
      await accountSettingsApi.updateSettings({
        monthlyBudgetLimit: budgetValue,
      });
      toast.success(t('whatsappConfig.budgetSaved', 'Budget limit saved successfully!'));
    } catch (error) {
      console.error('Failed to save budget limit:', error);
      toast.error(t('whatsappConfig.budgetError', 'Failed to save budget limit'));
    } finally {
      setIsSavingBudget(false);
    }
  };

  const handleRemoveBudget = async () => {
    try {
      setIsSavingBudget(true);
      await accountSettingsApi.updateSettings({
        monthlyBudgetLimit: null,
      });
      setMonthlyBudgetLimit("");
      toast.success(t('whatsappConfig.budgetRemoved', 'Budget limit removed'));
    } catch (error) {
      console.error('Failed to remove budget limit:', error);
      toast.error(t('whatsappConfig.budgetError', 'Failed to update budget'));
    } finally {
      setIsSavingBudget(false);
    }
  };

  const handleTestConnection = () => {
    if (!userData?.phoneNumber) {
      toast.error(t('whatsappConfig.phoneNumberRequired'));
      return;
    }
    
    // Simulate test connection
    toast.success(t('whatsappConfig.testConnectionSuccess'));
  };

  const handleSaveConfig = () => {
    if (isEnabled && !userData?.phoneNumber) {
      toast.error(t('whatsappConfig.phoneNumberRequired'));
      return;
    }
    
    // Here you would save the configuration to your backend
    toast.success(t('whatsappConfig.configSaved'));
  };

  // Show loading state until we have user data
  if (isLoadingUser) {
    return (
      <div className="page-shell">
        {/* Header Skeleton */}
        <div className={`flex items-center justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
          <div className={`space-y-2 ${isRTL ? 'text-right' : 'text-left'}`}>
            <Skeleton className="h-8 w-64" />
            <Skeleton className="h-4 w-80" />
          </div>
          <Skeleton className="h-6 w-20" />
        </div>

        {/* Main Card Skeleton */}
        <Card>
          <CardHeader>
            <Skeleton className="h-6 w-32" />
            <Skeleton className="h-4 w-48" />
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Toggle Section Skeleton */}
            <div className={`flex items-center justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
              <div className="space-y-1">
                <Skeleton className="h-4 w-48" />
                <Skeleton className="h-3 w-64" />
              </div>
              <Skeleton className="h-6 w-12" />
            </div>
            
            {/* Phone Number Section Skeleton */}
            <div className="space-y-2">
              <Skeleton className="h-4 w-32" />
              <Skeleton className="h-10 w-full max-w-md" />
              <Skeleton className="h-3 w-56" />
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
      <div className="page-shell">
      {/* Header */}
      <div className={`page-heading ${isRTL ? 'flex-row-reverse' : ''}`}>
        <div className={isRTL ? 'text-right' : 'text-left'}>
          <h1 className={`text-3xl font-bold flex items-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
            <MessageSquare className="h-8 w-8 text-green-600" />
            {t('whatsappConfig.title')}
          </h1>
          <p className={`text-muted-foreground mt-1 ${isRTL ? 'text-right' : 'text-left'}`}>
            {t('whatsappConfig.subtitle')}
          </p>
        </div>
        <Badge variant={userData?.whatsappEnabled ? "default" : "secondary"} className="px-3 py-1">
          {userData?.whatsappEnabled ? (
            <>
              <CheckCircle className="h-4 w-4 mr-1" />
              {t('whatsappConfig.enabled')}
            </>
          ) : (
            <>
              <AlertCircle className="h-4 w-4 mr-1" />
              {t('whatsappConfig.disabled')}
            </>
          )}
        </Badge>
      </div>

      {/* iPhone Automation Guide Promo Card */}
      <Card className="border border-primary/30 bg-primary/8">
        <CardContent className="pt-6">
          <div className={`flex items-start gap-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
            <div className="flex-shrink-0 w-14 h-14 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center shadow-lg">
              <Smartphone className="h-7 w-7 text-white" />
            </div>
            <div className={`flex-1 ${isRTL ? 'text-right' : 'text-left'}`}>
              <h3 className="text-xl font-bold mb-2">
                {isRTL ? "💡 האם יש לך אייפון?" : "💡 Have an iPhone?"}
              </h3>
              <p className="text-muted-foreground mb-4">
                {isRTL
                  ? "הגדר אוטומציה באייפון שלך כך שכל תשלום ב-Apple Pay יישלח אוטומטית לפינג'נט דרך WhatsApp. חסוך זמן ותעקוב אחרי ההוצאות בקלות!"
                  : "Set up iPhone automation so every Apple Pay payment is automatically sent to Fingent via WhatsApp. Save time and track expenses effortlessly!"}
              </p>
              <div className={`flex gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
                <Button
                  onClick={() => navigate('/iphone-automation-guide')}
                  className="gap-2"
                  variant="default"
                >
                  <BookOpen className="h-4 w-4" />
                  {isRTL ? "לחץ למדריך מפורט" : "View Detailed Guide"}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => navigate('/iphone-automation-guide')}
                  className="gap-2"
                >
                  <Sparkles className="h-4 w-4" />
                  {isRTL ? "איך זה עובד?" : "How It Works"}
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Main Configuration */}
      <Card className="border-border/90">
        <CardHeader>
          <CardTitle className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse text-right' : 'text-left'}`}>
            <Settings className="h-5 w-5" />
            {t('whatsappConfig.configuration')}
          </CardTitle>
          <CardDescription className={isRTL ? 'text-right' : 'text-left'}>
            {t('whatsappConfig.configDescription')}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Enable/Disable Toggle */}
          <div className={`flex items-center justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
            <div className={`space-y-0.5 ${isRTL ? 'text-right' : 'text-left'}`}>
              <Label className="text-base font-medium">
                {t('whatsappConfig.enableFeature')}
              </Label>
              <p className="text-sm text-muted-foreground">
                {t('whatsappConfig.enableDescription')}
              </p>
            </div>
            <Switch
              checked={isEnabled}
              onCheckedChange={handleEnableToggle}
            />
          </div>

          {/* Transaction Notifications Toggle - Only show when WhatsApp is connected via API */}
          {userData?.whatsappEnabled && (
            <div className={`flex items-center justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
              <div className={`space-y-0.5 ${isRTL ? 'text-right' : 'text-left'}`}>
                <Label className="text-base font-medium">
                  {t('whatsappConfig.transactionNotifications')}
                </Label>
                <p className="text-sm text-muted-foreground">
                  {t('whatsappConfig.transactionNotificationsDescription')}
                </p>
              </div>
              <Switch
                checked={transactionNotificationsEnabled}
                onCheckedChange={handleTransactionNotificationsToggle}
                disabled={isLoadingSettings}
              />
            </div>
          )}

          {/* Budget Notifications Toggle - Only show when WhatsApp is connected via API */}
          {userData?.whatsappEnabled && (
            <div className={`flex items-center justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
              <div className={`space-y-0.5 ${isRTL ? 'text-right' : 'text-left'}`}>
                <Label className="text-base font-medium">
                  {t('whatsappConfig.budgetNotificationsTitle', 'Budget Notifications')}
                </Label>
                <p className="text-sm text-muted-foreground">
                  {t('whatsappConfig.budgetNotificationsToggleDescription', 'Get WhatsApp alerts at 90% and 100% of your budget limit')}
                </p>
              </div>
              <Switch
                checked={!!monthlyBudgetLimit}
                onCheckedChange={handleBudgetNotificationsToggle}
                disabled={isLoadingSettings || isSavingBudget}
              />
            </div>
          )}

          {/* Monthly Budget Limit Section - Collapsible based on toggle state */}
          {monthlyBudgetLimit && (
            <>
            <Separator />
            <div className={`space-y-4 ${isRTL ? 'text-right' : 'text-left'} animate-fadeIn`}>
              {/* Header with inline budget input */}
              <div className={`flex items-center justify-between gap-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
                <div className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <Target className="h-5 w-5 text-amber-500" />
                  <h3 className="text-base font-medium">
                    {t('whatsappConfig.monthlyBudgetTitle', 'Monthly Budget Limit')}
                  </h3>
                </div>
                
                {/* Budget Input - inline with header */}
                <div className={`flex gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <div className="relative">
                    <Input
                      type="number"
                      min="0"
                      step="1"
                      placeholder={isRTL ? '5000' : '5000'}
                      value={monthlyBudgetLimit}
                      onChange={(e) => setMonthlyBudgetLimit(e.target.value)}
                      className={`w-40 ${isRTL ? 'text-right pr-8' : 'pl-8'}`}
                      disabled={isLoadingSettings}
                    />
                    <span className={`absolute top-1/2 -translate-y-1/2 text-muted-foreground text-sm ${isRTL ? 'right-2' : 'left-2'}`}>
                      {isRTL ? '₪' : '$'}
                    </span>
                  </div>
                  <Button
                    onClick={handleSaveBudget}
                    disabled={isSavingBudget || isLoadingSettings}
                    variant="default"
                    size="sm"
                  >
                    {isSavingBudget ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      t('common.save', 'Save')
                    )}
                  </Button>
                </div>
              </div>

              {/* AI Calculation Loading State */}
            {isLoadingSettings && (
              <div className="bg-gradient-to-r from-purple-50 to-indigo-50 dark:from-purple-950/20 dark:to-indigo-950/20 border border-purple-200 dark:border-purple-800 rounded-xl p-4">
                <div className="space-y-4">
                  {/* Header with Animation */}
                  <div className={`flex items-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <div className="w-10 h-10 bg-purple-100 dark:bg-purple-900/30 rounded-full flex items-center justify-center flex-shrink-0 relative">
                      <Sparkles className="h-5 w-5 text-purple-600 dark:text-purple-400 animate-pulse" />
                      <div className="absolute inset-0 rounded-full border-2 border-purple-400 border-t-transparent animate-spin"></div>
                    </div>
                    <div className={isRTL ? 'text-right' : 'text-left'}>
                      <h4 className="font-medium text-purple-700 dark:text-purple-400 flex items-center gap-2">
                        {t('whatsappConfig.calculatingTitle', 'Analyzing Your Finances')}
                        <span className="inline-flex">
                          <span className="animate-bounce" style={{ animationDelay: '0ms' }}>.</span>
                          <span className="animate-bounce" style={{ animationDelay: '150ms' }}>.</span>
                          <span className="animate-bounce" style={{ animationDelay: '300ms' }}>.</span>
                        </span>
                      </h4>
                      <p className="text-sm text-muted-foreground">
                        {t('whatsappConfig.calculatingDescription', 'Our AI is analyzing your transaction history to provide personalized budget recommendations.')}
                      </p>
                    </div>
                  </div>

                  {/* Skeleton Stats Grid */}
                  <div className={`grid grid-cols-1 sm:grid-cols-3 gap-4`}>
                    <div className="bg-green-50/50 dark:bg-green-950/20 rounded-lg p-3 animate-pulse">
                      <div className="h-3 w-24 bg-green-200 dark:bg-green-800 rounded mb-2"></div>
                      <div className="h-6 w-20 bg-green-300 dark:bg-green-700 rounded"></div>
                    </div>
                    <div className="bg-red-50/50 dark:bg-red-950/20 rounded-lg p-3 animate-pulse">
                      <div className="h-3 w-24 bg-red-200 dark:bg-red-800 rounded mb-2"></div>
                      <div className="h-6 w-20 bg-red-300 dark:bg-red-700 rounded"></div>
                    </div>
                    <div className="bg-purple-100/50 dark:bg-purple-900/20 rounded-lg p-3 border-2 border-purple-200 dark:border-purple-800 animate-pulse">
                      <div className="h-3 w-28 bg-purple-200 dark:bg-purple-700 rounded mb-2"></div>
                      <div className="h-6 w-20 bg-purple-300 dark:bg-purple-600 rounded"></div>
                    </div>
                  </div>

                  {/* Loading Progress Bar */}
                  <div className="relative h-1 bg-purple-100 dark:bg-purple-900 rounded-full overflow-hidden">
                    <div className="absolute inset-y-0 left-0 bg-gradient-to-r from-purple-400 to-purple-600 rounded-full animate-loading-bar"></div>
                  </div>
                </div>
              </div>
            )}

            {/* AI Suggestion Card - Only show if we have data and not loading */}
            {!isLoadingSettings && (averageMonthlyExpenses || averageMonthlyIncome) && averageMonthsCount && (
              <div className="bg-gradient-to-r from-purple-50 to-indigo-50 dark:from-purple-950/20 dark:to-indigo-950/20 border border-purple-200 dark:border-purple-800 rounded-lg p-3 animate-fadeIn">
                <div className={`space-y-3`}>
                  {/* Header */}
                  <div className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <Sparkles className="h-4 w-4 text-purple-600 dark:text-purple-400" />
                    <h4 className="text-sm font-medium text-purple-700 dark:text-purple-400">
                      {t('whatsappConfig.aiSuggestionTitle', 'AI Budget Suggestion')}
                    </h4>
                    <span className="text-xs text-muted-foreground">
                      ({t('whatsappConfig.basedOnMonths', '{{months}} months', { months: averageMonthsCount })})
                    </span>
                  </div>

                  {/* Stats Grid - More compact */}
                  <div className={`grid grid-cols-3 gap-2 ${isRTL ? 'direction-rtl' : ''}`}>
                    {/* Average Income */}
                    <div className={`bg-green-50 dark:bg-green-950/30 rounded-md p-2 ${isRTL ? 'text-right' : 'text-left'}`}>
                      <p className="text-[10px] text-green-600 dark:text-green-400 font-medium">
                        {t('whatsappConfig.avgIncome', 'Avg. Income')}
                      </p>
                      <p className="text-base font-bold text-green-700 dark:text-green-400">
                        {isRTL ? '₪' : '$'}{(averageMonthlyIncome || 0).toLocaleString()}
                      </p>
                    </div>

                    {/* Average Expenses */}
                    <div className={`bg-red-50 dark:bg-red-950/30 rounded-md p-2 ${isRTL ? 'text-right' : 'text-left'}`}>
                      <p className="text-[10px] text-red-600 dark:text-red-400 font-medium">
                        {t('whatsappConfig.avgExpenses', 'Avg. Expenses')}
                      </p>
                      <p className="text-base font-bold text-red-700 dark:text-red-400">
                        {isRTL ? '₪' : '$'}{(averageMonthlyExpenses || 0).toLocaleString()}
                      </p>
                    </div>

                    {/* Suggested Budget (75% of income) */}
                    <div className={`bg-purple-100 dark:bg-purple-900/40 rounded-md p-2 border border-purple-300 dark:border-purple-700 ${isRTL ? 'text-right' : 'text-left'}`}>
                      <p className="text-[10px] text-purple-600 dark:text-purple-400 font-medium">
                        {t('whatsappConfig.suggestedBudget', 'Suggested (75%)')}
                      </p>
                      <p className="text-base font-bold text-purple-700 dark:text-purple-400">
                        {isRTL ? '₪' : '$'}{(suggestedBudgetLimit || 0).toLocaleString()}
                      </p>
                    </div>
                  </div>

                  {/* Apply Button */}
                  {suggestedBudgetLimit && (
                    <div className={`flex ${isRTL ? 'justify-start' : 'justify-end'}`}>
                      <Button
                        onClick={handleApplySuggestion}
                        variant="default"
                        size="sm"
                        className="bg-purple-600 hover:bg-purple-700 text-white h-7 text-xs px-2"
                      >
                        <Sparkles className={`h-3 w-3 ${isRTL ? 'ml-1' : 'mr-1'}`} />
                        {t('whatsappConfig.applySuggestion', 'Use This')}
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* No Data Message - Show if no transactions yet and not loading */}
            {!isLoadingSettings && !averageMonthlyExpenses && !averageMonthlyIncome && (
              <div className="bg-muted/50 border border-border rounded-xl p-4">
                <div className={`flex items-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <div className="w-10 h-10 bg-muted rounded-full flex items-center justify-center flex-shrink-0">
                    <Sparkles className="h-5 w-5 text-muted-foreground" />
                  </div>
                  <div className={isRTL ? 'text-right' : 'text-left'}>
                    <h4 className="font-medium text-muted-foreground">
                      {t('whatsappConfig.noDataTitle', 'AI Suggestion Coming Soon')}
                    </h4>
                    <p className="text-sm text-muted-foreground">
                      {t('whatsappConfig.noDataDescription', 'Once you have transaction history, we\'ll calculate your average income and expenses to help you set a budget.')}
                    </p>
                  </div>
                </div>
              </div>
            )}

            </div>
            </>
          )}

          <Separator />

          {/* Phone Number Display - Always show */}
          <div className={`space-y-2 ${isRTL ? 'text-right' : 'text-left'}`}>
            <div className={`flex items-center gap-2 text-base font-medium ${isRTL ? 'flex-row-reverse' : ''}`}>
              <Phone className="h-4 w-4" />
              {t('whatsappConfig.phoneNumber')}
            </div>
            <div className={`bg-muted p-3 rounded-lg ${isRTL ? 'text-right' : 'text-left'}`}>
              {isLoadingUser ? (
                <div className="text-sm text-muted-foreground">
                  {t('common.loading')}
                </div>
              ) : userData?.phoneNumber ? (
                <div className={`text-sm font-mono font-semibold ${isRTL ? 'text-right' : 'text-left'}`}>
                  {userData.phoneNumber}
                </div>
              ) : (
                <div className="text-sm text-destructive">
                  {t('whatsappConfig.noPhoneNumber')}
                </div>
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              {t('whatsappConfig.phoneNumberFromProfile')}
            </p>
          </div>

          {/* WhatsApp Connected Status - Only when API confirms WhatsApp is enabled */}
          {userData?.whatsappEnabled && (
            <>
              <Separator />
              
              {/* Connected Animation */}
              <div className="relative overflow-hidden">
                <div className="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-950/20 dark:to-emerald-950/20 border-2 border-green-200 dark:border-green-800 rounded-2xl p-8">
                  {/* Animated background circles */}
                  <div className="absolute top-0 right-0 w-40 h-40 bg-green-200/30 rounded-full blur-3xl animate-pulse"></div>
                  <div className="absolute bottom-0 left-0 w-32 h-32 bg-emerald-200/30 rounded-full blur-3xl animate-pulse delay-700"></div>
                  
                  <div className="relative z-10 text-center space-y-6">
                    {/* Success Icon with Animation */}
                    <div className="flex justify-center">
                      <div className="relative">
                        <div className="w-24 h-24 bg-green-500 rounded-full flex items-center justify-center animate-scale-in">
                          <Check className="h-12 w-12 text-white" strokeWidth={3} />
                        </div>
                        <div className="absolute inset-0 w-24 h-24 bg-green-400/50 rounded-full animate-ping"></div>
                      </div>
                    </div>
                    
                    {/* Status Text */}
                    <div className="space-y-2">
                      <h3 className={`text-2xl font-bold text-green-700 dark:text-green-400 ${isRTL ? 'text-right' : 'text-center'}`}>
                        {t('whatsappConfig.connectedTitle')}
                      </h3>
                      <p className={`text-muted-foreground ${isRTL ? 'text-right' : 'text-center'}`}>
                        {t('whatsappConfig.connectedDescription')}
                      </p>
                    </div>
                    
                    {/* Features List */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-4">
                      <div className={`flex items-center gap-3 p-3 bg-white/50 dark:bg-gray-800/50 rounded-lg ${isRTL ? 'flex-row-reverse' : ''}`}>
                        <div className="flex-shrink-0 w-10 h-10 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center">
                          <MessageSquare className="h-5 w-5 text-green-600 dark:text-green-400" />
                        </div>
                        <div className={isRTL ? 'text-right' : 'text-left'}>
                          <p className="font-medium text-sm">{t('whatsappConfig.featureMessages')}</p>
                        </div>
                      </div>
                      
                      <div className={`flex items-center gap-3 p-3 bg-white/50 dark:bg-gray-800/50 rounded-lg ${isRTL ? 'flex-row-reverse' : ''}`}>
                        <div className="flex-shrink-0 w-10 h-10 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center">
                          <Zap className="h-5 w-5 text-green-600 dark:text-green-400" />
                        </div>
                        <div className={isRTL ? 'text-right' : 'text-left'}>
                          <p className="font-medium text-sm">{t('whatsappConfig.featureInstant')}</p>
                        </div>
                      </div>
                      
                      <div className={`flex items-center gap-3 p-3 bg-white/50 dark:bg-gray-800/50 rounded-lg ${isRTL ? 'flex-row-reverse' : ''}`}>
                        <div className="flex-shrink-0 w-10 h-10 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center">
                          <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400" />
                        </div>
                        <div className={isRTL ? 'text-right' : 'text-left'}>
                          <p className="font-medium text-sm">{t('whatsappConfig.featureAuto')}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Setup Instructions - Show when switch is enabled but WhatsApp not yet connected via API */}
          {isEnabled && !userData?.whatsappEnabled && (
            <>
              <Separator />
              
              {/* Two Options Layout */}
              <div className={`grid grid-cols-1 lg:grid-cols-2 gap-6 ${isRTL ? 'direction-rtl' : ''}`}>
                {/* Option 1: QR Code - Quick Way */}
                <div className="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-950/20 dark:to-emerald-950/20 border-2 border-green-200 dark:border-green-800 rounded-2xl p-6">
                  <div className={`flex items-center gap-2 mb-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
                      <QrCode className="h-4 w-4 text-white" />
                    </div>
                    <div className={isRTL ? 'text-right' : 'text-left'}>
                      <h3 className="font-semibold text-green-700 dark:text-green-400">
                        {t('whatsappConfig.optionQrTitle')}
                      </h3>
                      <p className="text-xs text-muted-foreground">{t('whatsappConfig.optionQrSubtitle')}</p>
                    </div>
                  </div>
                  
                  <div className="flex flex-col items-center space-y-4">
                    <div className="bg-white p-4 rounded-xl shadow-md">
                      <QRCode
                        value={generateWhatsAppURL()}
                        size={180}
                        level="M"
                      />
                    </div>
                    <p className={`text-sm text-muted-foreground text-center ${isRTL ? 'text-right' : ''}`}>
                      {t('whatsappConfig.qrCodeDescription')}
                    </p>
                  </div>
                </div>
                
                {/* Option 2: Manual Instructions */}
                <div className="bg-muted/50 border border-border rounded-2xl p-6">
                  <div className={`flex items-center gap-2 mb-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center">
                      <ExternalLink className="h-4 w-4 text-white" />
                    </div>
                    <div className={isRTL ? 'text-right' : 'text-left'}>
                      <h3 className="font-semibold">
                        {t('whatsappConfig.optionManualTitle')}
                      </h3>
                      <p className="text-xs text-muted-foreground">{t('whatsappConfig.optionManualSubtitle')}</p>
                    </div>
                  </div>
                  
                  <div className={`space-y-4 ${isRTL ? 'text-right' : 'text-left'}`}>
                    <div className={`flex gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
                      <div className="flex-shrink-0 w-6 h-6 bg-primary/10 rounded-full flex items-center justify-center text-xs font-semibold text-primary">
                        1
                      </div>
                      <div className={isRTL ? 'text-right' : 'text-left'}>
                        <p className="text-sm">
                          {t('whatsappConfig.manualStep1', { phoneNumber: WHATSAPP_CONFIG.agentNumberWithPlus })}
                        </p>
                      </div>
                    </div>
                    
                    <div className={`flex gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
                      <div className="flex-shrink-0 w-6 h-6 bg-primary/10 rounded-full flex items-center justify-center text-xs font-semibold text-primary">
                        2
                      </div>
                      <div className={isRTL ? 'text-right' : 'text-left'}>
                        <p className="text-sm">
                          {t('whatsappConfig.manualStep2')}
                        </p>
                      </div>
                    </div>
                    
                    <div className={`flex gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
                      <div className="flex-shrink-0 w-6 h-6 bg-primary/10 rounded-full flex items-center justify-center text-xs font-semibold text-primary">
                        3
                      </div>
                      <div className={`flex-1 ${isRTL ? 'text-right' : 'text-left'}`}>
                        <p className="text-sm mb-2">
                          {t('whatsappConfig.manualStep3')}
                        </p>
                        {/* Verification Message Box */}
                        <div className={`flex items-center gap-2 bg-background border rounded-lg p-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                          <code className="flex-1 text-sm font-mono text-primary bg-primary/5 px-2 py-1 rounded">
                            {getVerificationMessage()}
                          </code>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={handleCopyMessage}
                            className="flex-shrink-0 h-8 w-8 p-0"
                          >
                            <Copy className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                    
                    <div className="pt-2">
                      <Button
                        onClick={() => window.open(generateWhatsAppURL(), '_blank')}
                        className="w-full"
                        disabled={!userData}
                        variant="outline"
                      >
                        <MessageSquare className={`h-4 w-4 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                        {t('whatsappConfig.openWhatsApp')}
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Verify Button - Centered below both options */}
              <div className="flex justify-center pt-4">
                <Button
                  onClick={handleVerifyConnection}
                  size="lg"
                  disabled={!userData || isVerifying}
                  className="min-w-[200px]"
                >
                  {isVerifying ? (
                    <Loader2 className={`h-4 w-4 animate-spin ${isRTL ? 'ml-2' : 'mr-2'}`} />
                  ) : (
                    <ShieldCheck className={`h-4 w-4 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                  )}
                  {t('whatsappConfig.verifyConnection')}
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Example Messages - Only when WhatsApp is connected via API */}
      {userData?.whatsappEnabled && (
        <Card className="border-border/90">
          <CardHeader>
            <CardTitle className={isRTL ? 'text-right' : 'text-left'}>
              {t('whatsappConfig.exampleMessages')}
            </CardTitle>
            <CardDescription className={isRTL ? 'text-right' : 'text-left'}>
              {t('whatsappConfig.exampleDescription')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="bg-muted p-4 rounded-lg">
                <p className={`text-sm font-mono ${isRTL ? 'text-right' : 'text-left'}`}>
                  {isRTL ? '"הוצאתי 80 שקל על קניות בסופר"' : '"Spent $25 on groceries at Walmart"'}
                </p>
              </div>
              <div className="bg-muted p-4 rounded-lg">
                <p className={`text-sm font-mono ${isRTL ? 'text-right' : 'text-left'}`}>
                  {isRTL ? '"קפה 18 שקל - קפה גרג"' : '"Coffee $4.50 - Starbucks"'}
                </p>
              </div>
              <div className="bg-muted p-4 rounded-lg">
                <p className={`text-sm font-mono ${isRTL ? 'text-right' : 'text-left'}`}>
                  {isRTL ? '"תחנת דלק 200 שקל דלק"' : '"Gas station $60 fuel"'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Disable Confirmation Dialog */}
      <AlertDialog open={showDisableDialog} onOpenChange={setShowDisableDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className={isRTL ? 'text-right' : 'text-left'}>
              {t('whatsappConfig.disableConfirmTitle')}
            </AlertDialogTitle>
            <AlertDialogDescription className={isRTL ? 'text-right' : 'text-left'}>
              {t('whatsappConfig.disableConfirmMessage')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleCancelDisable}>
              {t('common.cancel')}
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmDisable} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              {t('whatsappConfig.disableConfirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
} 